export const investingLearningSources = [
  { label: 'SEC Investor.gov｜定期定額的定義', url: 'https://www.investor.gov/introduction-investing/investing-basics/glossary/dollar-cost-averaging' },
  { label: 'SEC Investor.gov｜費用對投資成果的影響', url: 'https://www.investor.gov/introduction-investing/general-resources/news-alerts/alerts-bulletins/investor-bulletins/updated' },
  { label: '嘉信理財研究｜退休提領的報酬順序風險', url: 'https://www.schwab.com/learn/story/timing-matters-understanding-sequence-returns-risk' },
];

const finite = value => typeof value === 'number' && Number.isFinite(value);
const allFinite = values => values.every(finite);
const cleanZero = value => value === 0 ? 0 : value;

/**
 * Compare uses of the SAME budget already available before period 1.
 * Buy at each period's observed price, then value holdings at that same price.
 * Cash earns nothing, fractional units are allowed, and there are no transaction
 * costs, dividends, taxes or further external cash flows. Rows start at period 1.
 */
export function purchasePlan(parameters = {}) {
  const { prices, budget, mode } = parameters || {};
  if (!Array.isArray(prices) || prices.length < 2 || prices.length > 24
    || !finite(budget) || budget <= 0 || !['upfront', 'spread'].includes(mode)) return null;
  for (const price of prices) if (!finite(price) || price <= 0) return null;
  const regularContribution = budget / prices.length;
  let invested = 0;
  let units = 0;
  const rows = [];
  for (let index = 0; index < prices.length; index += 1) {
    const price = prices[index];
    const last = index === prices.length - 1;
    const contribution = mode === 'upfront'
      ? (index === 0 ? budget : 0)
      : (last ? budget - invested : regularContribution);
    units += contribution / price;
    invested = last || mode === 'upfront' ? budget : invested + contribution;
    const cash = cleanZero(budget - invested);
    const totalValue = units * price + cash;
    if (!allFinite([contribution, units, invested, cash, totalValue]) || contribution < 0 || cash < 0) return null;
    rows.push({ period: index + 1, price, contribution, invested, cash, units, totalValue });
  }
  const averageCost = budget / units;
  const endValue = rows[rows.length - 1].totalValue;
  const returnPercent = cleanZero((endValue / budget - 1) * 100);
  if (units <= 0 || averageCost <= 0 || !allFinite([averageCost, returnPercent])) return null;
  return { rows, units, averageCost, endValue, returnPercent };
}

/**
 * Constant hypothetical annual gross return, followed by a year-end asset fee.
 * The fee applies to the entire post-return balance, not just investment gains.
 * Inputs annualReturn and annualFee use percentages. No contributions, tax or
 * other fees are modeled. Rows include year 0. feesPaid is cumulative cash fees;
 * wealthGap also reflects what those removed amounts would subsequently earn/lose.
 */
export function feeGrowth(parameters = {}) {
  const { principal, annualReturn, annualFee, years } = parameters || {};
  if (!finite(principal) || principal < 0 || !finite(annualReturn) || annualReturn <= -100 || annualReturn > 100
    || !finite(annualFee) || annualFee < 0 || annualFee > 100
    || !Number.isInteger(years) || years < 0 || years > 50) return null;
  const growthFactor = 1 + annualReturn / 100;
  const feeRate = annualFee / 100;
  let noFee = principal;
  let afterFee = principal;
  let totalFees = 0;
  const rows = [{ year: 0, noFee, afterFee, feesPaid: 0 }];
  for (let year = 1; year <= years; year += 1) {
    noFee *= growthFactor;
    const beforeFee = afterFee * growthFactor;
    const fee = beforeFee * feeRate;
    afterFee = beforeFee - fee;
    totalFees += fee;
    if (!allFinite([noFee, beforeFee, fee, afterFee, totalFees])) return null;
    rows.push({ year, noFee, afterFee, feesPaid: totalFees });
  }
  const wealthGap = cleanZero(noFee - afterFee);
  if (!finite(wealthGap)) return null;
  return { rows, endValue: afterFee, noFeeValue: noFee, totalFees, wealthGap };
}

/**
 * Apply each annual return first, then request the same nominal withdrawal.
 * The investor can receive no more than the available balance. Zero is absorbing:
 * no debt, additional contributions, inflation or return from outside capital.
 * Each return is a percentage. This is a path comparison, not a safe-withdrawal model.
 */
export function withdrawalPath(parameters = {}) {
  const { principal, returns, withdrawal } = parameters || {};
  if (!finite(principal) || principal <= 0 || !finite(withdrawal) || withdrawal < 0
    || !Array.isArray(returns) || returns.length < 1 || returns.length > 50) return null;
  for (const returnRate of returns) if (!finite(returnRate) || returnRate < -100 || returnRate > 100) return null;
  let ending = principal;
  let totalPaid = 0;
  let totalShortfall = 0;
  let depletedYear = null;
  const rows = [];
  for (let index = 0; index < returns.length; index += 1) {
    const year = index + 1;
    const returnRate = returns[index];
    const opening = ending;
    const afterReturn = opening * (1 + returnRate / 100);
    if (!finite(afterReturn)) return null;
    const paid = Math.min(afterReturn, withdrawal);
    const shortfall = cleanZero(withdrawal - paid);
    ending = cleanZero(afterReturn - paid);
    totalPaid += paid;
    totalShortfall += shortfall;
    if (!allFinite([paid, shortfall, ending, totalPaid, totalShortfall])) return null;
    if (ending === 0 && depletedYear === null) depletedYear = year;
    rows.push({ year, returnRate, opening, afterReturn, requested: withdrawal, paid, shortfall, ending });
  }
  return { rows, ending, totalPaid, totalShortfall, depletedYear };
}
